/**
 * Factories Selectors
 */

import { createSelector } from '@ufc/core'
import type { FactoriesState } from './factories.slice'

// Root state selector (assumes factories is at state.factories)
interface RootState {
  factories: FactoriesState
}

// Base selectors
export const selectFactoriesState = (state: RootState) => state.factories
export const selectFactoriesItems = (state: RootState) => state.factories.items
export const selectSelectedFactory = (state: RootState) => state.factories.selectedFactory
export const selectFactoriesStats = (state: RootState) => state.factories.stats
export const selectFactoriesFilter = (state: RootState) => state.factories.filter
export const selectFactoriesPagination = (state: RootState) => state.factories.pagination
export const selectFactoriesLoading = (state: RootState) => state.factories.loading
export const selectFactoriesError = (state: RootState) => state.factories.error
export const selectCountries = (state: RootState) => state.factories.countries
export const selectCities = (state: RootState) => state.factories.cities
export const selectLocations = (state: RootState) => state.factories.locations

// Memoized selectors
export const selectActiveFactories = createSelector([selectFactoriesItems], (items) =>
  items.filter((factory) => factory.status === 'active')
)

export const selectInactiveFactories = createSelector([selectFactoriesItems], (items) =>
  items.filter((factory) => factory.status === 'inactive')
)

export const selectFactoriesByStatus = createSelector(
  [selectFactoriesItems, (_state: RootState, status: 'active' | 'inactive' | 'all') => status],
  (items, status) => {
    if (status === 'all') return items
    return items.filter((factory) => factory.status === status)
  }
)

export const selectFactoryByDocumentId = createSelector(
  [selectFactoriesItems, (_state: RootState, documentId: string) => documentId],
  (items, documentId) => items.find((factory) => factory.documentId === documentId) ?? null
)

export const selectFactoriesCount = createSelector([selectFactoriesItems], (items) => items.length)

export const selectHasFactories = createSelector(
  [selectFactoriesItems],
  (items) => items.length > 0
)

export const selectFilteredFactories = createSelector(
  [selectFactoriesItems, selectFactoriesFilter],
  (items, filter) => {
    let filtered = items

    // Filter by status
    if (filter.status && filter.status !== 'all') {
      filtered = filtered.filter((factory) => factory.status === filter.status)
    }

    // Filter by search
    if (filter.search) {
      const searchLower = filter.search.toLowerCase()
      filtered = filtered.filter(
        (factory) =>
          factory.name.toLowerCase().includes(searchLower) ||
          factory.code.toLowerCase().includes(searchLower) ||
          factory.address.toLowerCase().includes(searchLower) ||
          factory.city.toLowerCase().includes(searchLower) ||
          factory.country.toLowerCase().includes(searchLower)
      )
    }

    return filtered
  }
)

export const selectIsLoading = createSelector([selectFactoriesLoading], (loading) => loading)

export const selectHasError = createSelector([selectFactoriesError], (error) => error !== null)

// ============================================================================
// Factory Selection Selectors (for Gate Portal header)
// ============================================================================

/**
 * Get the currently selected factory (for display in header)
 */
export const selectCurrentFactory = (state: RootState) => state.factories.currentFactory

/**
 * Get the currently selected factory ID
 */
export const selectCurrentFactoryId = (state: RootState) => state.factories.currentFactoryId

/**
 * Get the list of factories for the dropdown
 */
export const selectFactoriesList = (state: RootState) => state.factories.factoriesList

/**
 * Get factory context for X-Factory-Id header injection
 * Returns documentId for Strapi v5 compatibility
 */
export const selectFactoryContext = (state: RootState): string | null => {
  return state.factories.currentFactoryId
}

/**
 * Check if a factory is currently selected
 */
export const selectHasFactorySelected = createSelector(
  [selectCurrentFactory],
  (factory) => factory !== null
)

/**
 * Get display name for the current factory (or "All Factories" if none)
 */
export const selectFactoryDisplayName = createSelector(
  [selectCurrentFactory],
  (factory) => factory?.name ?? null
)

/**
 * City search results for factory form drawer
 */
export const selectCitiesSearchResults = (state: RootState) => state.factories.citiesSearchResults

/**
 * Loading state for city search in factory form drawer
 */
export const selectCitiesSearchLoading = (state: RootState) => state.factories.citiesSearchLoading

/**
 * Whether more cities are available to load
 */
export const selectCitiesHasMore = (state: RootState) => state.factories.citiesHasMore

/**
 * Loading state for load-more cities
 */
export const selectCitiesLoadMoreLoading = (state: RootState) => state.factories.citiesLoadMoreLoading
